import { Controller } from "@hotwired/stimulus"

import { formatTime } from "@/lib/utils"

interface TrackInfo {
  title: string
  creatorName: string
  durationSeconds: number
  imageUrl: string
}

export default class extends Controller {
  static targets = [
    "controls",
    "title",
    "creator",
    "artwork",
    "artworkPlaceholder",
    "progressBar",
    "progressFill",
    "currentTime",
    "totalDuration",
    "previousButton",
    "nextButton",
    "playPauseButton",
    "playPauseIcon",
  ]

  declare readonly controlsTarget: HTMLElement
  declare readonly hasControlsTarget: boolean
  declare readonly titleTarget: HTMLElement
  declare readonly creatorTarget: HTMLElement
  declare readonly artworkTarget: HTMLImageElement
  declare readonly artworkPlaceholderTarget: HTMLElement
  declare readonly hasArtworkPlaceholderTarget: boolean
  declare readonly progressBarTarget: HTMLElement
  declare readonly hasProgressBarTarget: boolean
  declare readonly progressFillTarget: HTMLElement
  declare readonly currentTimeTarget: HTMLElement
  declare readonly totalDurationTarget: HTMLElement
  declare readonly previousButtonTarget: HTMLButtonElement
  declare readonly nextButtonTarget: HTMLButtonElement
  declare readonly playPauseButtonTarget: HTMLButtonElement
  declare readonly playPauseIconTarget: HTMLElement

  private hideTimer: ReturnType<typeof setTimeout> | null = null
  private wakeLockSentinel: WakeLockSentinel | null = null
  isActive = false

  disconnect() {
    this.clearHideTimer()
    this.releaseWakeLock()
  }

  enter(
    item: TrackInfo,
    hasPrevious: boolean,
    hasNext: boolean,
    playing: boolean,
  ) {
    this.isActive = true
    this.syncTrackInfo(item)
    this.syncTransport(hasPrevious, hasNext)
    this.updatePlayPauseIcon(playing)
    ;(this.element as HTMLElement).style.display = ""
    this.showControls()
    this.scheduleHide()
    void this.requestWakeLock()
  }

  exit() {
    if (!this.isActive) return
    this.isActive = false
    ;(this.element as HTMLElement).style.display = "none"
    this.clearHideTimer()
    this.releaseWakeLock()
  }

  syncTrackInfo(item: TrackInfo) {
    this.titleTarget.textContent = item.title
    this.creatorTarget.textContent = item.creatorName
    this.totalDurationTarget.textContent = formatTime(item.durationSeconds)
    this.currentTimeTarget.textContent = "0:00"

    if (item.imageUrl) {
      this.artworkTarget.src = item.imageUrl
      this.artworkTarget.classList.remove("hidden")
      if (this.hasArtworkPlaceholderTarget)
        this.artworkPlaceholderTarget.classList.add("hidden")
    } else {
      this.artworkTarget.classList.add("hidden")
      if (this.hasArtworkPlaceholderTarget)
        this.artworkPlaceholderTarget.classList.remove("hidden")
    }
  }

  syncTransport(hasPrevious: boolean, hasNext: boolean) {
    this.previousButtonTarget.disabled = !hasPrevious
    this.nextButtonTarget.disabled = !hasNext
  }

  updateProgress(ratio: number, currentTime: number, duration: number) {
    if (!this.hasProgressBarTarget) return
    this.progressFillTarget.style.width = `${ratio * 100}%`
    this.currentTimeTarget.textContent = formatTime(currentTime)
    if (duration) this.totalDurationTarget.textContent = formatTime(duration)
  }

  updatePlayPauseIcon(playing: boolean) {
    this.playPauseIconTarget.setAttribute("name", playing ? "pause" : "play")
  }

  revealControls() {
    if (!this.isActive) return
    this.showControls()
    this.scheduleHide()
  }

  // --- private ---

  private showControls() {
    if (this.hasControlsTarget) this.controlsTarget.style.display = ""
  }

  private hideControls() {
    if (this.hasControlsTarget) this.controlsTarget.style.display = "none"
  }

  private scheduleHide() {
    this.clearHideTimer()
    this.hideTimer = setTimeout(() => this.hideControls(), 3000)
  }

  private clearHideTimer() {
    if (this.hideTimer) {
      clearTimeout(this.hideTimer)
      this.hideTimer = null
    }
  }

  private async requestWakeLock() {
    if ("wakeLock" in navigator) {
      try {
        this.wakeLockSentinel = await navigator.wakeLock.request("screen")
      } catch {
        // Wake Lock not available or denied
      }
    }
  }

  private releaseWakeLock() {
    if (this.wakeLockSentinel) {
      void this.wakeLockSentinel.release()
      this.wakeLockSentinel = null
    }
  }
}
